import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../../services/apiClient';
import { useAuth } from '../../context/AuthContext';
import { isPlatformWorkspaceUser } from '../../utils/platformWorkspace';
import { formatRoleLabel } from '../../config/appRoutes';
import Button from '../ui/Button';
import Pill from '../ui/Pill';
import StatusBanner from '../ui/StatusBanner';
import './DigestConfigPanel.css';

/**
 * Daily Workforce Compliance Digest — configuration panel.
 *
 * Mounts inside the CompanyManagementModal next to PlatformBillingTab.
 * UI-only; uses either the tenant-scoped /api/digests/* endpoints
 * or the platform-delegated /api/platform/companies/:companyId/
 * digests/* endpoints depending on the mounted surface.
 *
 * Permission: read endpoints require employees.read OR manage_users;
 * writes require manage_users. The route layer enforces this; the
 * panel surfaces an error banner if the call comes back 403.
 *
 * Tenant scope: tenant routes still derive companyId from req.user.
 * When mounted in Platform Admin, the parent passes companyId and the
 * panel calls the platform-delegated digest routes directly.
 */

const KIND = 'daily_workforce';
const TENANT_BASE_URL = `/api/digests/subscriptions/${KIND}`;

const ROLE_OPTIONS = [
  { value: 'director', label: formatRoleLabel('director') },
  { value: 'airport_manager', label: formatRoleLabel('airport_manager') },
  { value: 'ops_manager', label: formatRoleLabel('ops_manager') }
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({
  value: h,
  label: `${String(h).padStart(2, '0')}:00`
}));

function STATUS_TONE(status) {
  if (status === 'sent') return 'success';
  if (status === 'partial' || status === 'skipped') return 'warning';
  if (status === 'failed') return 'danger';
  return 'neutral';
}

function formatRelative(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function DigestConfigPanel({ companyId = null, companyName }) {
  const auth = useAuth();
  const authUser = auth?.user || null;
  const normalizedCompanyId = Number(companyId);
  const isPlatformDelegatedConfig = Number.isFinite(normalizedCompanyId) && normalizedCompanyId > 0;
  const baseUrl = useMemo(() => (
    isPlatformDelegatedConfig
      ? `/api/platform/companies/${normalizedCompanyId}/digests/subscriptions/${KIND}`
      : TENANT_BASE_URL
  ), [isPlatformDelegatedConfig, normalizedCompanyId]);

  const isOnPlatformOwnContext = useMemo(
    () => Boolean(authUser
      && isPlatformWorkspaceUser(authUser)
      && !authUser.viewingClientPortal
      && !isPlatformDelegatedConfig),
    [authUser, isPlatformDelegatedConfig]
  );

  const [subscription, setSubscription] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [pendingRoleAdd, setPendingRoleAdd] = useState('');
  const [pendingExternalEmail, setPendingExternalEmail] = useState('');

  // Local mirror of the editable subscription fields. Apply via Save.
  const [form, setForm] = useState({
    enabled: false,
    sendHourLocal: 6,
    sendWhenEmpty: true
  });

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await apiRequest(`${baseUrl}`);
      setSubscription(data?.subscription || null);
      setRecipients(Array.isArray(data?.recipients) ? data.recipients : []);
      if (data?.subscription) {
        setForm({
          enabled: data.subscription.enabled === true,
          sendHourLocal: Number.isFinite(data.subscription.sendHourLocal) ? data.subscription.sendHourLocal : 6,
          sendWhenEmpty: data.subscription.sendWhenEmpty !== false
        });
      }
    } catch (err) {
      setError(err?.message || 'Unable to load digest configuration.');
    } finally {
      setIsLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => { refetch(); }, [refetch]);

  /**
   * UX hardening — ensure a subscription row exists before any
   * mutation. If the panel state shows no subscription yet, issue
   * the PUT with the current form values; otherwise no-op. Returns
   * true if a row exists (or was just created) and false if the
   * implicit save failed.
   *
   * The server's POST /recipients endpoint also auto-creates as a
   * defense-in-depth; this client-side path keeps the UX coherent
   * (the panel reflects the freshly created row + its enabled state
   * immediately).
   */
  const ensureSubscriptionExists = useCallback(async () => {
    if (subscription) return true;
    try {
      await apiRequest(baseUrl, {
        method: 'PUT',
        body: JSON.stringify({
          enabled: form.enabled === true,
          cadence: 'daily',
          sendHourLocal: Number(form.sendHourLocal),
          sendWhenEmpty: form.sendWhenEmpty === true
        })
      });
      await refetch();
      return true;
    } catch (err) {
      setError(err?.message || 'Unable to initialize digest subscription.');
      return false;
    }
  }, [baseUrl, form, refetch, subscription]);

  const handleSave = useCallback(async () => {
    setBusy('save');
    setError('');
    setInfo('');
    try {
      await apiRequest(baseUrl, {
        method: 'PUT',
        body: JSON.stringify({
          enabled: form.enabled === true,
          cadence: 'daily',
          sendHourLocal: Number(form.sendHourLocal),
          sendWhenEmpty: form.sendWhenEmpty === true
        })
      });
      setInfo('Saved digest configuration.');
      await refetch();
    } catch (err) {
      setError(err?.message || 'Unable to save digest configuration.');
    } finally {
      setBusy('');
    }
  }, [baseUrl, form, refetch]);

  const handleAddRoleRecipient = useCallback(async () => {
    if (!pendingRoleAdd) return;
    setBusy(`add-role-${pendingRoleAdd}`);
    setError('');
    setInfo('');
    try {
      // UX hardening — auto-create the subscription on first use so
      // the operator never sees "subscription does not exist".
      const ok = await ensureSubscriptionExists();
      if (!ok) return;
      await apiRequest(`${baseUrl}/recipients`, {
        method: 'POST',
        body: JSON.stringify({ recipientKind: 'role', role: pendingRoleAdd })
      });
      setInfo(`Added ${pendingRoleAdd.replace('_', ' ')} role recipient.`);
      setPendingRoleAdd('');
      await refetch();
    } catch (err) {
      setError(err?.message || 'Unable to add role recipient.');
    } finally {
      setBusy('');
    }
  }, [baseUrl, pendingRoleAdd, refetch, ensureSubscriptionExists]);

  const handleAddExternalRecipient = useCallback(async () => {
    const email = String(pendingExternalEmail || '').trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid email address.');
      return;
    }
    setBusy('add-external');
    setError('');
    setInfo('');
    try {
      // UX hardening — auto-create the subscription on first use.
      const ok = await ensureSubscriptionExists();
      if (!ok) return;
      await apiRequest(`${baseUrl}/recipients`, {
        method: 'POST',
        body: JSON.stringify({ recipientKind: 'external_email', email })
      });
      setInfo(`Added ${email} as a digest recipient.`);
      setPendingExternalEmail('');
      await refetch();
    } catch (err) {
      setError(err?.message || 'Unable to add external recipient.');
    } finally {
      setBusy('');
    }
  }, [baseUrl, pendingExternalEmail, refetch, ensureSubscriptionExists]);

  const handleRemoveRecipient = useCallback(async (recipientId) => {
    setBusy(`remove-${recipientId}`);
    setError('');
    setInfo('');
    try {
      await apiRequest(`${baseUrl}/recipients/${recipientId}`, { method: 'DELETE' });
      setInfo('Recipient removed.');
      await refetch();
    } catch (err) {
      setError(err?.message || 'Unable to remove recipient.');
    } finally {
      setBusy('');
    }
  }, [baseUrl, refetch]);

  const handlePreview = useCallback(async () => {
    setBusy('preview');
    setError('');
    setInfo('');
    setPreviewSubject('');
    setPreviewHtml('');
    try {
      const data = await apiRequest(`${baseUrl}/preview`, { method: 'POST' });
      setPreviewSubject(data?.rendered?.subject || '');
      setPreviewHtml(data?.rendered?.html || '');
      setShowPreview(true);
      setInfo('Preview rendered.');
    } catch (err) {
      setError(err?.message || 'Unable to render preview.');
    } finally {
      setBusy('');
    }
  }, [baseUrl]);

  const handleSendTest = useCallback(async () => {
    const target = String(testEmail || '').trim().toLowerCase();
    if (!target || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(target)) {
      setError('Enter a valid email address for the test send.');
      return;
    }
    setBusy('send-test');
    setError('');
    setInfo('');
    try {
      // UX hardening — auto-create the subscription on first use so
      // the test send doesn't fail with "subscription does not exist".
      const ok = await ensureSubscriptionExists();
      if (!ok) return;
      const data = await apiRequest(`${baseUrl}/send-test`, {
        method: 'POST',
        body: JSON.stringify({ email: target })
      });
      const result = data?.result || {};
      if (result.skipped) {
        setInfo(`Test skipped: ${result.reason || 'unknown'}.`);
      } else {
        setInfo(`Test send: ${result.sent} of ${result.recipientCount} delivered (status=${result.status}).`);
      }
    } catch (err) {
      setError(err?.message || 'Unable to send test.');
    } finally {
      setBusy('');
    }
  }, [baseUrl, testEmail, ensureSubscriptionExists]);

  const existingRoles = useMemo(
    () => new Set(recipients.filter((r) => r.recipientKind === 'role').map((r) => r.role)),
    [recipients]
  );

  const availableRoleOptions = useMemo(
    () => ROLE_OPTIONS.filter((opt) => !existingRoles.has(opt.value)),
    [existingRoles]
  );

  useEffect(() => {
    if (isOnPlatformOwnContext) return;
    if (!pendingRoleAdd && availableRoleOptions.length > 0) {
      setPendingRoleAdd(availableRoleOptions[0].value);
    }
  }, [availableRoleOptions, isOnPlatformOwnContext, pendingRoleAdd]);

  useEffect(() => {
    if (isOnPlatformOwnContext) return;
    const suggestedEmail = String(authUser?.email || '').trim().toLowerCase();
    if (!suggestedEmail) return;
    if (!testEmail) {
      setTestEmail(suggestedEmail);
    }
  }, [authUser, isOnPlatformOwnContext, testEmail]);

  const isClean = subscription
    && subscription.enabled === form.enabled
    && Number(subscription.sendHourLocal) === Number(form.sendHourLocal)
    && (subscription.sendWhenEmpty !== false) === (form.sendWhenEmpty === true);
  const isMutationLocked = !isPlatformDelegatedConfig && isOnPlatformOwnContext;
  const hasRecipientChoices = availableRoleOptions.length > 0;
  const panelDescription = isPlatformDelegatedConfig
    ? "Configure this customer's daily workforce digest."
    : 'Configure this tenant’s daily workforce digest.';

  return (
    <section
      className="director-card digest-config-panel"
      data-testid="digest-config-panel"
    >
      <div className="role-section-header">
        <h2>Daily Workforce Compliance Digest</h2>
        {subscription ? (
          <Pill variant={subscription.enabled ? 'success' : 'neutral'}>
            {subscription.enabled ? 'Enabled' : 'Disabled'}
          </Pill>
        ) : (
          <Pill variant="neutral">Not configured</Pill>
        )}
      </div>
      <p className="digest-config-help-copy">{panelDescription}</p>

      {isOnPlatformOwnContext ? (
        <div
          className="digest-config-tenant-warning"
          data-testid="digest-config-platform-context-warning"
        >
          <div className="digest-config-tenant-warning-copy">
            <strong>Switch to the client portal to edit this digest.</strong>
            <p className="digest-config-tenant-warning-text">
              You are currently configuring VizionOps. Enter the customer client portal to unlock Save settings, Add role, Add email, and Send test.
            </p>
          </div>
        </div>
      ) : null}

      {!isLoading && !subscription ? (
        <p
          className="digest-config-empty-state-banner"
          data-testid="digest-config-not-configured-banner"
        >
          This digest has not been configured yet. Save settings to initialize it for this tenant.
        </p>
      ) : null}

      {error ? <StatusBanner kind="error" message={error} live /> : null}
      {info ? <StatusBanner kind="success" message={info} live /> : null}

      {isLoading ? (
        <div className="digest-config-loading">Loading digest configuration…</div>
      ) : (
        <>
          <div className="digest-config-form-grid">
            <label className="digest-config-toggle">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                data-testid="digest-config-enabled"
              />
              Send daily workforce compliance digest
            </label>
            <label>
              Send hour (tenant local)
              <select
                value={form.sendHourLocal}
                onChange={(e) => setForm({ ...form, sendHourLocal: Number(e.target.value) })}
                data-testid="digest-config-send-hour"
              >
                {HOUR_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <label className="digest-config-toggle">
              <input
                type="checkbox"
                checked={form.sendWhenEmpty}
                onChange={(e) => setForm({ ...form, sendWhenEmpty: e.target.checked })}
                data-testid="digest-config-send-when-empty"
              />
              Send digest even when there are no items
            </label>
          </div>
          {isMutationLocked ? (
            <p className="digest-config-help-copy">
              Editing is locked until you switch into the selected client portal.
            </p>
          ) : null}
          <div className="digest-config-form-actions">
            <Button
              size="sm"
              variant="primary"
              onClick={handleSave}
              disabled={busy === 'save' || isClean || isMutationLocked}
              data-testid="digest-config-save"
              title={isMutationLocked ? 'Enter the customer client portal first.' : undefined}
            >
              {busy === 'save' ? 'Saving…' : 'Save settings'}
            </Button>
            {subscription?.lastSentAt ? (
              <div
                className="digest-config-last-sent"
                data-testid="digest-config-last-sent"
              >
                <span>Last sent: {formatRelative(subscription.lastSentAt)}</span>
                {subscription.lastSentStatus ? (
                  <Pill variant={STATUS_TONE(subscription.lastSentStatus)}>
                    {subscription.lastSentStatus}
                  </Pill>
                ) : null}
                {subscription.lastSentError ? (
                  <span className="digest-config-last-sent-error">
                    {subscription.lastSentError}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="digest-config-recipients">
            <div className="digest-config-section-header">
              <h3>Recipients</h3>
              <span>Roles and external recipients</span>
            </div>
            {recipients.length === 0 ? (
              <p className="digest-config-empty">No recipients configured yet. Add a role or external email to enable delivery.</p>
            ) : (
              <ul
                className="digest-config-recipient-list"
                data-testid="digest-config-recipient-list"
              >
                {recipients.map((r) => (
                  <li
                    key={r.id}
                    className="digest-config-recipient-row"
                    data-testid={`digest-config-recipient-${r.id}`}
                  >
                    <span className="digest-config-recipient-label">
                      {r.recipientKind === 'role' ? (
                        <>
                          <Pill variant="info">Role</Pill>
                          <span>{(r.role || '').replace(/_/g, ' ')}</span>
                        </>
                      ) : r.recipientKind === 'user' ? (
                        <>
                          <Pill variant="info">User</Pill>
                          <span>User #{r.userId}</span>
                        </>
                      ) : (
                        <>
                          <Pill variant="info">Email</Pill>
                          <span>{r.email}</span>
                        </>
                      )}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRemoveRecipient(r.id)}
                      disabled={busy === `remove-${r.id}`}
                      data-testid={`digest-config-remove-${r.id}`}
                    >
                      {busy === `remove-${r.id}` ? 'Removing…' : 'Remove'}
                    </Button>
                  </li>
                ))}
              </ul>
            )}

            <div className="digest-config-add-recipient">
              <div className="digest-config-add-row">
                <label>
                  Role recipient
                  <select
                    value={pendingRoleAdd}
                    onChange={(e) => setPendingRoleAdd(e.target.value)}
                    disabled={!hasRecipientChoices}
                    data-testid="digest-config-role-select"
                  >
                    <option value="">{hasRecipientChoices ? 'Select role…' : 'All role recipients already added'}</option>
                    {availableRoleOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </label>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={handleAddRoleRecipient}
                      disabled={!pendingRoleAdd || busy.startsWith('add-role') || isMutationLocked}
                      data-testid="digest-config-add-role"
                      title={isMutationLocked ? 'Enter the customer client portal first.' : undefined}
                    >
                  {busy.startsWith('add-role') ? 'Adding…' : 'Add role'}
                </Button>
              </div>
              <div className="digest-config-add-row">
                <label>
                  External email recipient
                  <input
                    type="email"
                    value={pendingExternalEmail}
                    onChange={(e) => setPendingExternalEmail(e.target.value)}
                    placeholder="ops@example.com"
                    data-testid="digest-config-external-email"
                  />
                </label>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={handleAddExternalRecipient}
                  disabled={!pendingExternalEmail || busy === 'add-external' || isMutationLocked}
                  data-testid="digest-config-add-external"
                  title={isMutationLocked ? 'Enter the customer client portal first.' : undefined}
                >
                  {busy === 'add-external' ? 'Adding…' : 'Add email'}
                </Button>
              </div>
            </div>
          </div>

          <div className="digest-config-actions">
            <div className="digest-config-preview-actions">
              <Button
                size="sm"
                variant="secondary"
                onClick={handlePreview}
                disabled={busy === 'preview'}
                data-testid="digest-config-preview"
              >
                {busy === 'preview' ? 'Rendering…' : 'Preview digest'}
              </Button>
            </div>
            <div className="digest-config-test-send">
              <label>
                Test send
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="you@vizionops.com"
                  data-testid="digest-config-test-email"
                />
              </label>
              <Button
                size="sm"
                variant="primary"
                onClick={handleSendTest}
                disabled={!testEmail || busy === 'send-test' || isMutationLocked}
                data-testid="digest-config-send-test"
                title={isMutationLocked ? 'Enter the customer client portal first.' : undefined}
              >
                {busy === 'send-test' ? 'Sending…' : 'Send test'}
              </Button>
            </div>
          </div>
          <p className="digest-config-help-copy">
            Use Preview for a read-only render. Test send delivers the current digest to the email above without changing recipients.
          </p>

          {showPreview && previewHtml ? (
            <div
              className="digest-config-preview-card"
              data-testid="digest-config-preview-card"
            >
              <div className="digest-config-preview-header">
                <div className="digest-config-preview-subject">
                  <span>Subject</span>
                  <strong>{previewSubject || '(no subject)'}</strong>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowPreview(false)}
                >
                  Close preview
                </Button>
              </div>
              <iframe
                title="Digest preview"
                className="digest-config-preview-frame"
                sandbox=""
                srcDoc={previewHtml}
                data-testid="digest-config-preview-iframe"
              />
            </div>
          ) : null}

          {companyName ? (
            <p className="digest-config-tenant-label">{companyName}</p>
          ) : null}
        </>
      )}
    </section>
  );
}

export default DigestConfigPanel;
